import React, { useEffect, useState } from 'react';
import axios from 'axios';
import AdminLayout from '../components/AdminLayout';
import TrackSearch from '../components/TrackSearch';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Checkbox } from '../components/ui/checkbox';
import { ScrollArea } from '../components/ui/scroll-area';
import AIVibeGenerator from '../components/admin/AIVibeGenerator';
import { Plus, Pencil, Trash2, Search, Music, UserCheck, X, GripVertical, Download, Calendar, Clock, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ADMIN_DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const ADMIN_SLOTS = ['morning', 'afternoon', 'evening', 'night'];

// Sortable Track Item
const SortableTrackItem = ({ track, index, onRemove, formatDuration }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: track.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/5 group bg-[#1A1A1A]"
    >
      <button {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing p-1 hover:bg-white/10 rounded">
        <GripVertical className="w-4 h-4 text-gray-500" />
      </button>
      <span className="text-gray-500 w-6 text-center text-sm">{index + 1}</span>
      {track.album_image && (
        <img src={track.album_image} alt={track.album} className="w-10 h-10 rounded" />
      )}
      <div className="flex-1 min-w-0">
        <p className="text-white text-sm font-medium truncate">{track.name}</p>
        <p className="text-gray-400 text-xs truncate">{track.artists?.join(', ')}</p>
      </div>
      <span className="text-gray-400 text-sm">{formatDuration(track.duration_ms)}</span>
      <Button
        variant="ghost"
        size="icon"
        onClick={() => onRemove(track.id)}
        className="opacity-0 group-hover:opacity-100 hover:bg-red-500/20"
      >
        <X className="w-4 h-4 text-red-400" />
      </Button>
    </div>
  );
};

const AdminPlaylistsPage = () => {
  const [activeTab, setActiveTab] = useState('list'); // 'list' | 'ai'
  const [playlists, setPlaylists] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [editingPlaylist, setEditingPlaylist] = useState(null);
  const [assigningPlaylist, setAssigningPlaylist] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    cover_image: '',
    assigned_clients: [],
    tracks: [],
    source_playlist_id: '',
  });
  const [ytPlaylistUrl, setYtPlaylistUrl] = useState('');
  const [ytPlaylistImporting, setYtPlaylistImporting] = useState(false);

  const [selectedClients, setSelectedClients] = useState([]);
  // Slot assignments shape: [{client_id, day, slot}, ...]
  const [slotAssignments, setSlotAssignments] = useState([]);
  const [pickerClientId, setPickerClientId] = useState('');
  const [pickerDay, setPickerDay] = useState('monday');
  const [pickerSlot, setPickerSlot] = useState('evening');

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [playlistsRes, clientsRes] = await Promise.all([
        axios.get(`${API}/admin/playlists`),
        axios.get(`${API}/admin/clients`)
      ]);
      setPlaylists(playlistsRes.data);
      // AI venues stream from the in-house library, not YouTube playlists,
      // so they must never appear in the playlist assignment picklist.
      setClients(clientsRes.data.filter(c => c.is_active !== false && (c.client_type || 'commercial') !== 'ai'));
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      cover_image: '',
      assigned_clients: [],
      tracks: [],
      source_playlist_id: '',
    });
    setEditingPlaylist(null);
  };

  const handleOpenDialog = (playlist = null) => {
    if (playlist) {
      setEditingPlaylist(playlist);
      setFormData({
        name: playlist.name || '',
        description: playlist.description || '',
        cover_image: playlist.cover_image || '',
        assigned_clients: playlist.assigned_clients || [],
        tracks: playlist.tracks || [],
        source_playlist_id: playlist.source_playlist_id || '',
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const handleOpenAssignDialog = async (playlist) => {
    setAssigningPlaylist(playlist);
    setSelectedClients(playlist.assigned_clients || []);
    // Load existing slot_assignments for this playlist
    try {
      const r = await axios.get(`${API}/admin/playlists/${playlist.id}/slot-assignments`);
      setSlotAssignments(r.data.slot_assignments || []);
    } catch (err) {
      setSlotAssignments([]);
    }
    setAssignDialogOpen(true);
  };

  const handleImportPlaylist = (imported) => {
    setFormData(prev => ({
      ...prev,
      name: prev.name || imported.name,
      description: prev.description || imported.description,
      cover_image: prev.cover_image || imported.cover_image,
      tracks: imported.tracks,
      source_playlist_id: imported.source_playlist_id || prev.source_playlist_id || '',
    }));
  };

  const handleImportYouTubePlaylist = async () => {
    const url = ytPlaylistUrl.trim();
    if (!url) return;
    setYtPlaylistImporting(true);
    try {
      const r = await axios.get(`${API}/youtube/playlist/import`, { params: { url } });
      const imported = r.data?.tracks || [];
      if (!imported.length) {
        toast.info('No music-category videos found in that playlist');
        return;
      }
      setFormData((prev) => {
        const seen = new Set(prev.tracks.map(t => t.id));
        const merged = [...prev.tracks];
        for (const t of imported) {
          if (!seen.has(t.id)) { merged.push(t); seen.add(t.id); }
        }
        return { ...prev, tracks: merged, source_playlist_id: r.data?.playlist?.id || prev.source_playlist_id };
      });
      setYtPlaylistUrl('');
      toast.success(`Imported ${imported.length} track${imported.length === 1 ? '' : 's'} from YouTube`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not import that YouTube playlist');
    } finally {
      setYtPlaylistImporting(false);
    }
  };

  const handleAddTrack = (track) => {
    if (formData.tracks.some(t => t.id === track.id)) {
      toast.error('Track already in playlist');
      return;
    }
    setFormData(prev => ({
      ...prev,
      tracks: [...prev.tracks, track]
    }));
  };

  const handleRemoveTrack = (trackId) => {
    setFormData(prev => ({
      ...prev,
      tracks: prev.tracks.filter(t => t.id !== trackId)
    }));
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      setFormData(prev => {
        const oldIndex = prev.tracks.findIndex(t => t.id === active.id);
        const newIndex = prev.tracks.findIndex(t => t.id === over.id);
        return {
          ...prev,
          tracks: arrayMove(prev.tracks, oldIndex, newIndex)
        };
      });
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!formData.name) {
      toast.error('Name is required');
      return;
    }

    try {
      if (editingPlaylist) {
        await axios.put(`${API}/admin/playlists/${editingPlaylist.id}`, formData);
        toast.success('Playlist updated');
      } else {
        await axios.post(`${API}/admin/playlists`, formData);
        toast.success('Playlist created');
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Failed to save playlist:', error);
      toast.error('Failed to save playlist');
    }
  };

  const handleAssignClients = async () => {
    if (!assigningPlaylist) return;
    try {
      // Persist slot-based assignments (this also auto-derives `assigned_clients` server-side)
      await axios.put(`${API}/admin/playlists/${assigningPlaylist.id}/slot-assignments`, {
        assignments: slotAssignments,
      });
      // Also persist any extra clients that aren't in any slot yet (legacy bare-assign)
      const extraClients = selectedClients.filter(
        (cid) => !slotAssignments.some((a) => a.client_id === cid),
      );
      if (extraClients.length > 0) {
        const union = Array.from(new Set([
          ...selectedClients,
          ...slotAssignments.map((a) => a.client_id),
        ]));
        await axios.post(`${API}/admin/playlists/${assigningPlaylist.id}/assign`, {
          client_ids: union,
        });
      }
      toast.success(`Assignments saved (${slotAssignments.length} slots)`);
      setAssignDialogOpen(false);
      setAssigningPlaylist(null);
      setSlotAssignments([]);
      fetchData();
    } catch (error) {
      toast.error('Failed to save assignments');
    }
  };

  const addSlotAssignment = () => {
    if (!pickerClientId) {
      toast.error('Pick a venue first');
      return;
    }
    const exists = slotAssignments.some(
      (a) => a.client_id === pickerClientId && a.day === pickerDay && a.slot === pickerSlot,
    );
    if (exists) {
      toast.error('Slot already assigned for this venue');
      return;
    }
    setSlotAssignments((prev) => [...prev, { client_id: pickerClientId, day: pickerDay, slot: pickerSlot }]);
    if (!selectedClients.includes(pickerClientId)) {
      setSelectedClients((prev) => [...prev, pickerClientId]);
    }
  };

  const removeSlotAssignment = (idx) => {
    setSlotAssignments((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleDelete = async (playlistId) => {
    if (!window.confirm('Delete this playlist?')) return;
    
    try {
      await axios.delete(`${API}/admin/playlists/${playlistId}`);
      toast.success('Playlist deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete playlist');
    }
  };

  const toggleClientSelection = (clientId) => {
    setSelectedClients(prev => 
      prev.includes(clientId) ? prev.filter(id => id !== clientId) : [...prev, clientId]
    );
  };

  const filteredPlaylists = playlists.filter(playlist => 
    playlist.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getClientNames = (clientIds) => {
    if (!clientIds || clientIds.length === 0) return 'None';
    const names = clientIds.map(id => clients.find(c => c.id === id)?.name || 'Unknown').slice(0, 2);
    if (clientIds.length > 2) return `${names.join(', ')} +${clientIds.length - 2}`;
    return names.join(', ');
  };

  const formatDuration = (ms) => {
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const getTotalDuration = (tracks) => {
    const total = tracks.reduce((acc, t) => acc + (t.duration_ms || 0), 0);
    const hours = Math.floor(total / 3600000);
    const minutes = Math.floor((total % 3600000) / 60000);
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes} min`;
  };

  return (
    <AdminLayout>
      <div className="p-6 lg:p-8 overflow-y-auto content-scroll" data-testid="admin-playlists-page">
        {/* Tab bar */}
        <div className="mb-6">
          <div className="inline-flex rounded-full bg-white/[0.04] border border-white/10 p-1" data-testid="admin-playlists-tabs">
            <button
              onClick={() => setActiveTab('list')}
              className={`px-4 py-2 text-sm font-semibold rounded-full transition-all ${
                activeTab === 'list' ? 'bg-[#FF007F] text-white' : 'text-gray-400 hover:text-white'
              }`}
              data-testid="tab-playlists-list"
            >
              Playlists
            </button>
            <button
              onClick={() => setActiveTab('ai')}
              className={`px-4 py-2 text-sm font-semibold rounded-full transition-all inline-flex items-center gap-1.5 ${
                activeTab === 'ai'
                  ? 'text-white'
                  : 'text-gray-400 hover:text-white'
              }`}
              style={activeTab === 'ai' ? {
                background: 'linear-gradient(90deg, #9D00FF 0%, #FF007F 100%)',
                boxShadow: '0 0 20px rgba(157,0,255,0.35)',
              } : undefined}
              data-testid="tab-ai-generator"
            >
              <Sparkles className="w-3.5 h-3.5" /> AI Generator
            </button>
          </div>
        </div>

        {activeTab === 'ai' && <AIVibeGenerator />}
        {activeTab === 'list' && (
        <>
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white font-['Outfit'] mb-2">Playlists</h1>
            <p className="text-gray-400">Create playlists by searching and adding songs</p>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                onClick={() => handleOpenDialog()}
                className="bg-[#FF007F] hover:bg-[#CC0066] text-black rounded-full"
                data-testid="add-playlist-button"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Playlist
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#121212] border-white/10 text-white max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
              <DialogHeader>
                <DialogTitle className="font-['Outfit']">
                  {editingPlaylist ? 'Edit Playlist' : 'Create New Playlist'}
                </DialogTitle>
              </DialogHeader>
              
              <div className="flex-1 overflow-y-auto space-y-4 mt-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Name *</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      placeholder="Playlist name"
                      className="bg-[#1A1A1A] border-white/10"
                      data-testid="playlist-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Cover Image URL</Label>
                    <Input
                      value={formData.cover_image}
                      onChange={(e) => setFormData({...formData, cover_image: e.target.value})}
                      placeholder="https://..."
                      className="bg-[#1A1A1A] border-white/10"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Input
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder="Describe the playlist"
                    className="bg-[#1A1A1A] border-white/10"
                  />
                </div>

                <div className="border-t border-white/10 pt-4">
                  <Label className="mb-2 block">Search & Add Songs</Label>
                  <TrackSearch 
                    onAddTrack={handleAddTrack}
                    existingTrackIds={formData.tracks.map(t => t.id)}
                    placeholder="Search for songs..."
                  />
                </div>

                {/* Import a YouTube Playlist */}
                <div className="border-t border-white/10 pt-4">
                  <Label className="mb-2 flex items-center gap-2">
                    <Download className="w-4 h-4 text-[#FF0000]" />
                    Import a YouTube Playlist
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      value={ytPlaylistUrl}
                      onChange={(e) => setYtPlaylistUrl(e.target.value)}
                      placeholder="Paste a YouTube playlist URL or ID"
                      className="bg-[#1A1A1A] border-white/10 flex-1"
                      data-testid="admin-yt-playlist-import-input"
                    />
                    <Button
                      onClick={handleImportYouTubePlaylist}
                      disabled={!ytPlaylistUrl.trim() || ytPlaylistImporting}
                      className="bg-[#FF0000] hover:bg-[#CC0000] text-white"
                      data-testid="admin-yt-playlist-import-button"
                    >
                      {ytPlaylistImporting ? 'Importing…' : 'Import'}
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">Only music-category videos are kept. Max 200 tracks.</p>
                </div>

                {formData.tracks.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-sm text-gray-400">
                        {formData.tracks.length} songs • {getTotalDuration(formData.tracks)} • Drag to reorder
                      </p>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setFormData(prev => ({ ...prev, tracks: [] }))}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      >
                        Clear All
                      </Button>
                    </div>
                    <ScrollArea className="h-64">
                      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                        <SortableContext items={formData.tracks.map(t => t.id)} strategy={verticalListSortingStrategy}>
                          <div className="space-y-1">
                            {formData.tracks.map((track, index) => (
                              <SortableTrackItem
                                key={track.id}
                                track={track}
                                index={index}
                                onRemove={handleRemoveTrack}
                                formatDuration={formatDuration}
                              />
                            ))}
                          </div>
                        </SortableContext>
                      </DndContext>
                    </ScrollArea>
                  </div>
                )}

                {formData.tracks.length === 0 && (
                  <div className="text-center py-8 text-gray-400">
                    <Music className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No songs added yet</p>
                    <p className="text-sm">Search above to add songs</p>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-4 pt-4 border-t border-white/10 mt-4">
                <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-white/10 text-white hover:bg-white/10">
                  Cancel
                </Button>
                <Button onClick={handleSubmit} className="bg-[#FF007F] hover:bg-[#CC0066] text-black" data-testid="save-playlist-button">
                  {editingPlaylist ? 'Update' : 'Create'} Playlist
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        <div className="relative mb-6">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search playlists..."
            className="bg-[#121212] border-white/10 pl-12 h-12"
          />
        </div>

        <div className="bg-[#121212] border border-white/5 rounded-2xl overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-white/5 hover:bg-transparent">
                <TableHead className="text-gray-400">Playlist</TableHead>
                <TableHead className="text-gray-400">Songs</TableHead>
                <TableHead className="text-gray-400">Assigned To</TableHead>
                <TableHead className="text-gray-400 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={4} className="text-center py-8 text-gray-400">Loading...</TableCell></TableRow>
              ) : filteredPlaylists.length === 0 ? (
                <TableRow><TableCell colSpan={4} className="text-center py-8 text-gray-400">No playlists found</TableCell></TableRow>
              ) : (
                filteredPlaylists.map((playlist) => (
                  <TableRow key={playlist.id} className="border-white/5">
                    <TableCell>
                      <div className="flex items-center gap-3">
                        {playlist.cover_image ? (
                          <img src={playlist.cover_image} alt={playlist.name} className="w-12 h-12 rounded-lg object-cover" />
                        ) : (
                          <div className="w-12 h-12 rounded-lg bg-white/5 flex items-center justify-center">
                            <Music className="w-5 h-5 text-gray-400" />
                          </div>
                        )}
                        <div>
                          <p className="font-medium text-white">{playlist.name}</p>
                          <p className="text-sm text-gray-400 truncate max-w-[200px]">{playlist.description}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell><span className="text-gray-300">{playlist.tracks?.length || 0} songs</span></TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="text-gray-300 text-sm">{getClientNames(playlist.assigned_clients)}</span>
                        <Button variant="ghost" size="sm" onClick={() => handleOpenAssignDialog(playlist)} className="hover:bg-white/10 h-8 px-2">
                          <UserCheck className="w-4 h-4 text-[#10B981]" />
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button variant="ghost" size="icon" onClick={() => handleOpenDialog(playlist)} className="hover:bg-white/10">
                          <Pencil className="w-4 h-4 text-gray-400" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(playlist.id)} className="hover:bg-red-500/20">
                          <Trash2 className="w-4 h-4 text-red-400" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
          <DialogContent className="bg-[#121212] border-white/10 text-white max-w-2xl">
            <DialogHeader>
              <DialogTitle className="font-['Outfit']">Assign Schedule for &quot;{assigningPlaylist?.name}&quot;</DialogTitle>
            </DialogHeader>
            <div className="mt-4 space-y-5">
              {/* Slot picker */}
              <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-4">
                <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#39FF14] mb-3 font-['JetBrains_Mono']">
                  <Calendar className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />
                  Add a venue × day × slot
                </p>
                <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_auto_auto] gap-2">
                  <select
                    value={pickerClientId}
                    onChange={(e) => setPickerClientId(e.target.value)}
                    className="bg-black/60 border border-white/10 text-white rounded-lg h-10 px-3 text-sm"
                    data-testid="slot-picker-client"
                  >
                    <option value="">— pick venue —</option>
                    {clients.map((c) => (
                      <option key={c.id} value={c.id}>{c.name || c.email}</option>
                    ))}
                  </select>
                  <select
                    value={pickerDay}
                    onChange={(e) => setPickerDay(e.target.value)}
                    className="bg-black/60 border border-white/10 text-white rounded-lg h-10 px-3 text-sm capitalize"
                    data-testid="slot-picker-day"
                  >
                    {ADMIN_DAYS.map((d) => <option key={d} value={d} className="capitalize">{d}</option>)}
                  </select>
                  <select
                    value={pickerSlot}
                    onChange={(e) => setPickerSlot(e.target.value)}
                    className="bg-black/60 border border-white/10 text-white rounded-lg h-10 px-3 text-sm capitalize"
                    data-testid="slot-picker-slot"
                  >
                    {ADMIN_SLOTS.map((s) => <option key={s} value={s} className="capitalize">{s}</option>)}
                  </select>
                  <Button onClick={addSlotAssignment} className="bg-[#39FF14] hover:bg-[#2dd60f] text-black font-bold h-10 px-4 text-xs tracking-wider" data-testid="slot-picker-add">
                    <Plus className="w-4 h-4 mr-1" /> ADD
                  </Button>
                </div>
              </div>

              {/* Current slot assignments */}
              <div>
                <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-gray-400 mb-2 font-['JetBrains_Mono']">
                  <Clock className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />
                  Scheduled slots ({slotAssignments.length})
                </p>
                {slotAssignments.length === 0 ? (
                  <p className="text-gray-500 text-sm italic py-3">No slots assigned. Use the picker above to add.</p>
                ) : (
                  <ScrollArea className="h-48 pr-3">
                    <div className="space-y-1.5">
                      {slotAssignments.map((a, idx) => {
                        const client = clients.find((c) => c.id === a.client_id);
                        return (
                          <div key={`${a.client_id}-${a.day}-${a.slot}-${idx}`} className="flex items-center justify-between gap-3 p-2.5 rounded-lg bg-white/[0.04] border border-white/5" data-testid={`slot-assignment-row-${idx}`}>
                            <div className="flex items-center gap-2.5 min-w-0">
                              <div className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                                {(client?.name || '?').charAt(0).toUpperCase()}
                              </div>
                              <div className="min-w-0">
                                <p className="text-white text-sm font-medium truncate font-['Outfit']">{client?.name || a.client_id}</p>
                                <p className="text-[10px] text-gray-500 uppercase tracking-wider font-['JetBrains_Mono']">{a.day} · {a.slot}</p>
                              </div>
                            </div>
                            <button onClick={() => removeSlotAssignment(idx)} className="text-gray-500 hover:text-red-400 transition-colors" data-testid={`slot-assignment-remove-${idx}`}>
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </ScrollArea>
                )}
              </div>

              {/* Bare assign (no slot) — legacy */}
              <details className="border-t border-white/5 pt-3">
                <summary className="text-[10px] uppercase tracking-[0.25em] text-gray-500 cursor-pointer hover:text-gray-300 font-['JetBrains_Mono']">
                  Or: just give access without a slot
                </summary>
                <div className="mt-3">
                  <ScrollArea className="h-40 pr-3">
                    <div className="space-y-1.5">
                      {clients.map((client) => (
                        <div key={client.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/5 cursor-pointer" onClick={() => toggleClientSelection(client.id)}>
                          <Checkbox checked={selectedClients.includes(client.id)} onCheckedChange={() => toggleClientSelection(client.id)} className="border-white/30 data-[state=checked]:bg-[#FF007F] data-[state=checked]:border-[#FF007F]" />
                          <div className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center text-white text-xs">{client.name?.charAt(0) || 'C'}</div>
                          <p className="text-white text-sm">{client.name}</p>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </div>
              </details>

              <div className="flex justify-between items-center pt-4 border-t border-white/10">
                <p className="text-sm text-gray-400">{slotAssignments.length} slot{slotAssignments.length === 1 ? '' : 's'} · {selectedClients.length} venue{selectedClients.length === 1 ? '' : 's'}</p>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => setAssignDialogOpen(false)} className="border-white/10 text-white hover:bg-white/10">Cancel</Button>
                  <Button onClick={handleAssignClients} className="bg-[#FF007F] hover:bg-[#CC0066] text-black" data-testid="assign-save">Save schedule</Button>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
        </>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminPlaylistsPage;
